import { ref, computed } from 'vue'

export type ThemePreference = 'system' | 'dark' | 'light'
export type ResolvedTheme = 'dark' | 'light'

// Must match the inline script in index.html (applied before first paint)
const STORAGE_KEY = 'bimdrop-theme'

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: light)') : null

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored
  } catch {
    // storage blocked: fall back to system
  }
  return 'system'
}

const preference = ref<ThemePreference>(readPreference())
const systemTheme = ref<ResolvedTheme>(media?.matches ? 'light' : 'dark')

const theme = computed<ResolvedTheme>(() =>
  preference.value === 'system' ? systemTheme.value : preference.value
)

function apply() {
  document.documentElement.dataset.theme = theme.value
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.value === 'dark' ? '#050506' : '#e7e8ec')
}

let initialized = false

export function useTheme() {
  if (!initialized && typeof document !== 'undefined') {
    initialized = true
    apply()
    media?.addEventListener('change', (e) => {
      systemTheme.value = e.matches ? 'light' : 'dark'
      apply()
    })
  }

  function setPreference(p: ThemePreference) {
    preference.value = p
    try {
      localStorage.setItem(STORAGE_KEY, p)
    } catch {
      // ignore
    }
    apply()
  }

  /** Cycles system → light → dark → system. */
  function cycle() {
    const order: ThemePreference[] = ['system', 'light', 'dark']
    setPreference(order[(order.indexOf(preference.value) + 1) % order.length])
  }

  return { preference, theme, setPreference, cycle }
}
